import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { historicalConversionService } from '../../services';

const gbp = (v) => (v === null || v === undefined ? '—' : `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const STATUS = { priced: 'Ready', no_price: 'No price', no_measure: 'No matching measure', price_conflict: 'Prices differ' };

const Prov = ({ src, testId }) => (src ? (
  <Badge variant="outline" className={src === 'user' ? 'ml-1 border-violet-300 bg-violet-50 text-violet-800' : 'ml-1 border-slate-300 text-slate-600'} data-testid={testId}>
    {src === 'user' ? 'Entered by user' : 'From source'}
  </Badge>
) : null);

export const SiteRow = ({ l, conversionId, canEdit, onSaved }) => {
  const [measure, setMeasure] = useState(l.measure_source === 'user' ? String(l.measure_unit_id) : '');
  const [price, setPrice] = useState(l.price_source === 'user' ? l.selling_price_incl_vat : '');
  const [busy, setBusy] = useState(false);
  const legacy = l.measure_options === undefined;  // frozen before provenance was recorded
  const needMeasure = !legacy && l.measure_source !== 'source';
  const needPrice = !legacy && l.price_source !== 'source';
  const priceSrc = l.price_source || (legacy && l.selling_price_incl_vat ? 'source' : null);
  const measureSrc = l.measure_source || (legacy && l.measure_label ? 'source' : null);
  const affected = (l.source_status || l.price_status) !== 'priced';
  const valid = (!price || /^\d{1,5}(\.\d{1,2})?$/.test(String(price))) && (measure || price);

  const call = async (fn) => {
    setBusy(true);
    try { onSaved(await fn()); } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not save');
    } finally { setBusy(false); }
  };
  const save = () => call(async () => {
    const r = await historicalConversionService.savePriceInput(conversionId, l.bp_id, {
      measure_unit_id: needMeasure && measure ? Number(measure) : null,
      selling_price_incl_vat: needPrice && price ? String(price) : null,
    });
    toast.success(`${l.product_name}: saved to the draft`);
    return r.line;
  });

  return (
    <tr className="border-t align-top" data-testid={`snapshot-site-row-${l.bp_id}`}>
      <td className="px-3 py-1.5">
        {l.product_name}
        {l.source_prices?.length > 1 && (
          <div className="text-xs text-amber-800" data-testid={`snapshot-site-source-prices-${l.bp_id}`}>
            Source prices: {l.source_prices_by_location ? Object.entries(l.source_prices_by_location).map(([k, v]) => `${k} ${gbp(v)}`).join(' · ') : l.source_prices.map(gbp).join(' · ')}
          </div>
        )}
      </td>
      <td className="px-3 py-1.5">
        {canEdit && needMeasure && affected && l.measure_options?.length ? (
          <Select value={measure} onValueChange={setMeasure}>
            <SelectTrigger className="h-8 w-40" data-testid={`snapshot-site-measure-select-${l.bp_id}`}><SelectValue placeholder={l.source_serving_ml ? `Source ${l.source_serving_ml} ml` : 'Choose measure'} /></SelectTrigger>
            <SelectContent>{l.measure_options.map((o) => <SelectItem key={o.id} value={String(o.id)}>{o.label} ({o.ml} ml)</SelectItem>)}</SelectContent>
          </Select>
        ) : <>{l.measure_label || '—'}{canEdit && needMeasure && affected && !l.measure_options?.length ? <span className="block text-xs text-slate-500">No measures set up for this product</span> : null}</>}
        <Prov src={measureSrc} testId={`snapshot-site-measure-prov-${l.bp_id}`} />
      </td>
      <td className="px-3 py-1.5 tabular-nums">
        {canEdit && needPrice && affected ? (
          <div className="flex items-center gap-1"><span className="text-slate-500">£</span>
            <Input className="h-8 w-24" inputMode="decimal" value={price || ''} onChange={(e) => setPrice(e.target.value.trim())} placeholder="0.00" data-testid={`snapshot-site-price-input-${l.bp_id}`} />
          </div>
        ) : (l.selling_price_incl_vat ? gbp(l.selling_price_incl_vat) : '—')}
        <Prov src={priceSrc} testId={`snapshot-site-price-prov-${l.bp_id}`} />
      </td>
      <td className="px-3 py-1.5">
        <Badge variant="outline" className={l.price_status === 'priced' ? 'border-emerald-300 text-emerald-800' : 'border-amber-300 text-amber-800'} data-testid={`snapshot-site-status-${l.bp_id}`}>{STATUS[l.price_status]}</Badge>
        {canEdit && affected && (
          <div className="mt-1 flex gap-1">
            <Button size="sm" className="h-7" disabled={!valid || busy} onClick={save} data-testid={`snapshot-site-save-btn-${l.bp_id}`}>{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}</Button>
            {l.user_input && <Button size="sm" variant="ghost" className="h-7" disabled={busy} onClick={() => call(async () => { await historicalConversionService.clearPriceInput(conversionId, l.bp_id); return null; })} data-testid={`snapshot-site-clear-btn-${l.bp_id}`}><Undo2 className="h-3 w-3" /></Button>}
          </div>
        )}
      </td>
    </tr>
  );
};
